import { api } from '../api';

export type ExportEstado = 'corriendo' | 'terminado' | 'error';

/**
 * Cómo llega el archivo. El server lo decide por tamaño: arriba del umbral no
 * se espera en pantalla (el padrón entero tarda más que cualquier modal) y el
 * link va por mail.
 */
export type ExportEntrega = 'directa' | 'mail';

export interface ExportJob {
  id?: string;
  _id?: string;
  tipo: string;
  estado: ExportEstado;
  total: number;
  procesados: number;
  filename?: string;
  error?: string;
  entrega?: ExportEntrega;
  /** A qué casilla se manda el link (siempre la del usuario que disparó). */
  notificarA?: string;
  /** Cuándo salió el mail con el link. Ausente = todavía no salió. */
  mailEnviadoAt?: string;
  /** Por qué no se pudo mandar. El archivo igual se generó. */
  mailError?: string;
}

export function exportJobId(job: ExportJob): string {
  return job.id ?? job._id ?? '';
}

/** Los filtros de una pantalla como strings, que es lo que espera el export
 *  async; los vacíos no viajan. Vivía en el Reporte ABM Web y ahora la comparte
 *  el Reporte de padrón. */
export function paramsExport(f: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(f)) {
    if (v) out[k] = String(v);
  }
  return out;
}

/** Dispara el export `tipo` (params por query). 202 + job. Idempotente. */
export async function dispararExport(
  tipo: string,
  params: Record<string, string> = {},
): Promise<ExportJob> {
  const { data } = await api.post<ExportJob>(`/exportaciones/${tipo}/disparar`, {}, { params });
  return data;
}

/** Último job del export `tipo` para esos params; 404 → null. */
export async function getExportLatest(
  tipo: string,
  params: Record<string, string> = {},
): Promise<ExportJob | null> {
  try {
    const { data } = await api.get<ExportJob>(`/exportaciones/${tipo}/jobs/latest`, { params });
    return data;
  } catch (err) {
    const e = err as { response?: { status?: number } };
    if (e.response?.status === 404) return null;
    throw err;
  }
}

/** Baja el CSV generado (blob). El server lo borra apenas termina la descarga. */
async function descargarCsvBlob(jobId: string): Promise<Blob> {
  const { data } = await api.get(`/exportaciones/job/${jobId}/descargar`, { responseType: 'blob' });
  return new Blob([data as BlobPart], { type: 'text/csv;charset=utf-8' });
}

/**
 * Baja el CSV y lo convierte a .xlsx EN EL CLIENTE (SheetJS cargado on-demand),
 * para que la conversión sea carga de la máquina del usuario y no del server.
 * `raw: true` evita que Excel rompa DNIs/teléfonos con ceros a la izquierda.
 */
export async function descargarComoXlsx(jobId: string, filenameCsv?: string): Promise<void> {
  const blob = await descargarCsvBlob(jobId);
  let texto = await blob.text();
  if (texto.charCodeAt(0) === 0xfeff) texto = texto.slice(1); // sacar BOM (Excel) antes de parsear
  const XLSX = await import('xlsx');
  const wb = XLSX.read(texto, { type: 'string', raw: true });
  const base = (filenameCsv ?? 'export').replace(/\.csv$/i, '');
  XLSX.writeFile(wb, `${base}.xlsx`);
}

/**
 * Baja el archivo del link que llegó por mail (VEN-595). Va por POST con el
 * token en el BODY a propósito: en la URL terminaría en el log de requests del
 * server y en APM, y es una credencial viva contra un CSV con datos personales.
 *
 * Acá NO se convierte a .xlsx: son los exports grandes (el padrón completo son
 * ~194.000 filas) y cargar eso en SheetJS cuelga la pestaña. El CSV sale con
 * BOM UTF-8, así que Excel lo abre bien igual.
 */
export async function descargarPorLink(jobId: string, token: string): Promise<void> {
  const { data, headers } = await api.post(
    `/exportaciones/descarga/${jobId}`,
    { token },
    { responseType: 'blob' },
  );
  const blob = new Blob([data as BlobPart], { type: 'text/csv;charset=utf-8' });

  const nombre = nombreDelHeader(String(headers['content-disposition'] ?? '')) ?? 'export.csv';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** `filename*=UTF-8''...` (RFC 5987) primero, y si no `filename="..."`. */
function nombreDelHeader(disposition: string): string | null {
  const rfc5987 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (rfc5987) return decodeURIComponent(rfc5987[1]);
  const simple = /filename="([^"]+)"/i.exec(disposition);
  return simple ? simple[1] : null;
}
